import { Form as AntdForm, Select } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import StepRowList from './StepRowList'
import type { StepOption } from './stepOptions'

type StepPickerListProps = {
  addLabel: ReactNode
  /** What one pick is, e.g. "Team member", for its Remove button. */
  itemLabel: string
  /** The form field holding the chosen ids. */
  name: string
  options: ReadonlyArray<StepOption>
  testId: string
}

/**
 * A list of people chosen from options, e.g. team members: a searchable picker
 * labelled "Name" per person, as on mobile, with Remove, and Add below. Someone already picked is not offered
 * again; empty rows are dropped on save.
 */
const StepPickerList = ({
  addLabel,
  itemLabel,
  name,
  options,
  testId
}: StepPickerListProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance()
  const chosen: Array<string | undefined> = AntdForm.useWatch(name, form) ?? []

  return (
    <StepRowList
      addLabel={addLabel}
      itemLabel={itemLabel}
      label={t(incidentReportsConstants.CASE_V2_NAME)}
      name={name}
      renderInput={(row, index, id) => (
        <AntdForm.Item name={row.name} noStyle>
          <Select
            // "Name" alone, on every row, wouldn't say whose name it is.
            aria-label={t(incidentReportsConstants.CASE_V2_NAME_OF_ROW, {
              label: itemLabel,
              number: index + 1
            })}
            data-testid={`${testId}-select-${index}`}
            id={id}
            optionFilterProp="label"
            options={options.filter(
              (option) =>
                option.value === chosen[index] || !chosen.includes(option.value)
            )}
            placeholder={t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)}
            showSearch
          />
        </AntdForm.Item>
      )}
      testId={testId}
    />
  )
}

export default StepPickerList
